import { ReactElement } from 'react';
import { Link, useNavigate } from 'react-router';
import { Flex, Stack, Table } from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { TableSortIcon } from '~/core/shared/components/Table/TableSortIcon';
import { OptionalFieldVisibility } from '../OptionalFieldVisibility';
import { FieldTableGenericExtends } from './types/FieldTableGenericExtends';
import { FieldTableProps } from './types/FieldTableProps';

export function FieldTable<D extends FieldTableGenericExtends>({
  columns,
  rowPath,
  rowKey,
  getRowProps,
  data,
  variant,
}: FieldTableProps<D>): ReactElement {
  const navigate = useNavigate();
  const { getHeaderGroups, getRowModel } = useReactTable<D>({
    autoResetPageIndex: false,
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const numberOfCols = getHeaderGroups()[0]?.headers.length ?? 0;

  return (
    <Stack direction="column" gap={4}>
      <Table.Root borderRadius={0} variant={variant}>
        <Table.Header>
          {getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.ColumnHeader key={header.id}>
                  <Flex>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() && (
                      <TableSortIcon
                        direction={header.column.getIsSorted()}
                        isActive={!!header.column.getIsSorted()}
                      />
                    )}
                  </Flex>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          ))}
        </Table.Header>

        <Table.Body>
          {getRowModel().rows.map((row) => (
            <OptionalFieldVisibility
              key={rowKey ? rowKey(row) : row.id}
              field={row.original}
            >
              <Table.Row
                onClick={(e) =>
                  rowPath && e.button === 0 && navigate(rowPath(row.original))
                }
                {...(getRowProps && getRowProps(row))}
                _hover={rowPath && { bg: 'gray.50' }}
                cursor={rowPath && 'pointer'}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell key={cell.id}>
                    {rowPath ? (
                      <Link to={rowPath(row.original)}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Link>
                    ) : (
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </>
                    )}
                  </Table.Cell>
                ))}
              </Table.Row>
            </OptionalFieldVisibility>
          ))}
          {getRowModel().rows.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={numberOfCols}>
                <Flex justifyContent="center">No records</Flex>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </Stack>
  );
}
